export const lowercaseLetters = 'abcdefghijklmnopqrstuvxyz';
export const ambiguousCharacters = 'b8g6i1lo0qds5z2';
export const numbers = '0123456789';
export const lowercaseLettersAndNumbers = lowercaseLetters + numbers;
export const unambiguousLetters = 'acefhjkmnprtuvxy';
export const unambiguousNumbers = '3479';
export const unambiguousLettersAndNumbers = unambiguousLetters + unambiguousNumbers;
export const allLatinLettersRegex = /[A-Za-zÀ-ÿ]+/g;
export const nonLatinLettersRegex = /[^A-Za-zÀ-ÿ]+/g;
export const allValidFileNameCharacters = /[!"#%&'()+,./0-9=@A-Z\[\]_a-z\{\}~-]+/g;

export const dateTimeRegex = /^\d{4}-\d\d-\d\d(?:T\d\d:\d\d:\d\d(?:\.\d{1,3})?Z)?$/;
export const emailRegex = /^[^ '"\<\>@]+@[^ '"\<\>@]+\.[^ '"\<\>@.]+$/;
export const millisecondsPerSecond = 1000;
export const secondsPerMinute = 60;
export const minutesPerHours = 60;
export const hoursPerDay = 24;

export const millisecondsPerMinute = millisecondsPerSecond * secondsPerMinute;
export const millisecondsPerHour = millisecondsPerMinute * minutesPerHours;
export const millisecondsPerDay = millisecondsPerHour * hoursPerDay;

export const secondsPerHour = secondsPerMinute * minutesPerHours;
export const secondsPerDay = secondsPerHour * hoursPerDay;

export const minutesPerDay = minutesPerHours * hoursPerDay;

export const emptyObject = {};
export const emptyArray = [] as any[];

// Cutoff date for onboarding v2. Advisors created after this date use the new onboarding flow.
export const ONBOARDING_V2_CUTOFF_TIMESTAMP = new Date('2026-01-27T00:00:00Z').getTime();

export const fakeLocalJwtTokenPrefix = 'FAKE+LOCAL+JWT+TOKEN';
export const localBypassAuthStorageKey = 'DevLocalBypassAuthPayload';

export const customHiddenFieldQueryParamPrefix = 'custom_';

export const CARRIER_NAMES = {
  ONE_AMERICA: 'OneAmerica',
  NATIONWIDE: 'Nationwide',
  CARESCOUT: 'CareScout',
  LINCOLN_FINANCIAL: 'Lincoln Financial',
  SECURIAN_FINANCIAL: 'Securian Financial',
  EQUITRUST: 'EquiTrust',
  NEW_YORK_LIFE: 'New York Life',
} as const;

// Quote age range based on current database quotes (all carriers combined)
export const QUOTE_AGE_RANGE = {
  min: 40,
  max: 75,
} as const;

export function isAgeInRange(age: number): boolean {
  return age >= QUOTE_AGE_RANGE.min && age <= QUOTE_AGE_RANGE.max;
}

// Carriers without quote creation (illustration generation) support
export const CARRIERS_WITHOUT_QUOTE_CREATION = [CARRIER_NAMES.NEW_YORK_LIFE] as string[];

// Carriers without application logic support
export const CARRIERS_WITHOUT_APPLICATION_LOGIC = [
  CARRIER_NAMES.LINCOLN_FINANCIAL,
  CARRIER_NAMES.NEW_YORK_LIFE,
] as const;

export const PREFILL_COMPLETION_STATUS = {
  IDLE: 'idle',
  PROCESSING: 'processing',
  PROCESSING_PARTNER: 'processing_partner',
  COMPLETED: 'completed',
  ERROR: 'error',
} as const;

export const PREFILL_FORM_TYPE = {
  UNDERWRITING: 'underwriting',
  APPLICATION: 'application',
} as const;

// Underwriting Generation Versions - use semver (major.minor.patch) per aiPromptType
// Increment when underwriting AI models or prompts are updated:
// - major: Breaking changes (complete model/approach overhaul)
// - minor: New features or significant improvements
// - patch: Bug fixes or minor tweaks
export const UNDERWRITING_PROMPT_VERSIONS: Record<string, string> = {
  'underwriting-nw-guidance': '1.1.0',
  'underwriting-nw-partner-guidance': '1.1.0',
  'underwriting-oa-guidance': '1.1.0',
  'underwriting-oa-partner-guidance': '1.1.0',
  'underwriting-cs-guidance': '1.1.0',
  'underwriting-lincoln-guidance': '1.1.0',
  'underwriting-securian-guidance': '1.1.0',
  'underwriting-equitrust-guidance': '1.1.0',
  'underwriting-nyl-asset-flex-guidance': '1.0.0',
};

// Default version for unknown prompt types
export const DEFAULT_UNDERWRITING_VERSION = '1.1.0';

/**
 * Compare two semver version strings
 * Returns: -1 if v1 < v2, 0 if v1 === v2, 1 if v1 > v2
 */
export function compareVersions(v1: string | undefined, v2: string | undefined): number {
  if (!v1 || !v2) return v1 === v2 ? 0 : !v1 ? -1 : 1;

  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;
    if (part1 < part2) return -1;
    if (part1 > part2) return 1;
  }
  return 0;
}

/**
 * Check if a version is outdated compared to the current version for an aiPromptType
 */
export function isOutdatedVersion(
  version: string | undefined,
  aiPromptType: string | undefined,
): boolean {
  if (!version || !aiPromptType) return false;
  const currentVersion = UNDERWRITING_PROMPT_VERSIONS[aiPromptType] || DEFAULT_UNDERWRITING_VERSION;
  return compareVersions(version, currentVersion) < 0;
}

// Coverage Limits by Benefit Pool
const CARESCOUT_DAILY_BENEFIT_AMOUNTS = {
  MINIMAL: [50],
  STANDARD: [50, 100],
  ENHANCED: [50, 100, 150, 200],
  MAX_ONLY: [100],
  MAX_FULL: [100, 150, 200],
} as const;

const COVERAGE_TIERS: Record<string, CoverageTier> = {
  standard: {
    50000: CARESCOUT_DAILY_BENEFIT_AMOUNTS.STANDARD,
    100000: CARESCOUT_DAILY_BENEFIT_AMOUNTS.ENHANCED,
    150000: CARESCOUT_DAILY_BENEFIT_AMOUNTS.ENHANCED,
    200000: CARESCOUT_DAILY_BENEFIT_AMOUNTS.ENHANCED,
  },
  minimal: {
    50000: CARESCOUT_DAILY_BENEFIT_AMOUNTS.MINIMAL,
    100000: CARESCOUT_DAILY_BENEFIT_AMOUNTS.STANDARD,
    150000: CARESCOUT_DAILY_BENEFIT_AMOUNTS.ENHANCED,
    200000: CARESCOUT_DAILY_BENEFIT_AMOUNTS.ENHANCED,
  },
  maximum: {
    50000: CARESCOUT_DAILY_BENEFIT_AMOUNTS.MAX_ONLY,
    100000: CARESCOUT_DAILY_BENEFIT_AMOUNTS.MAX_FULL,
    150000: CARESCOUT_DAILY_BENEFIT_AMOUNTS.MAX_FULL,
    200000: CARESCOUT_DAILY_BENEFIT_AMOUNTS.MAX_FULL,
  },
};

// Elimination Period Options
const CARESCOUT_ELIMINATION_PERIODS = {
  STANDARD: [90, 180] as EliminationPeriod[],
  SHORT: [90] as EliminationPeriod[],
} as const;

// Rule Sets
type RuleSetKey =
  | 'standard'
  | 'minimalWithStandardPeriod'
  | 'maxWithShortPeriod'
  | 'standardWithShortPeriod';

const CARESCOUT_RULE_SETS: Record<RuleSetKey, ProductRules> = {
  standard: {
    eliminationPeriod: CARESCOUT_ELIMINATION_PERIODS.STANDARD,
    combinationPermitted: COVERAGE_TIERS.standard,
  },
  minimalWithStandardPeriod: {
    eliminationPeriod: CARESCOUT_ELIMINATION_PERIODS.STANDARD,
    combinationPermitted: COVERAGE_TIERS.minimal,
  },
  maxWithShortPeriod: {
    eliminationPeriod: CARESCOUT_ELIMINATION_PERIODS.SHORT,
    combinationPermitted: COVERAGE_TIERS.maximum,
  },
  standardWithShortPeriod: {
    eliminationPeriod: CARESCOUT_ELIMINATION_PERIODS.SHORT,
    combinationPermitted: COVERAGE_TIERS.standard,
  },
};

// State-specific product configurations
const CARESCOUT_STATE_CONFIGS = {
  // Standard ICC24-1110 states with standard rules
  STANDARD_ICC: [
    'AL',
    'AR',
    'GA',
    'IA',
    'ID',
    'IL',
    'KY',
    'LA',
    'ME',
    'MI',
    'MO',
    'MS',
    'NC',
    'NM',
    'NV',
    'OH',
    'OK',
    'RI',
    'TN',
    'TX',
    'UT',
    'VA',
    'WV',
    'WY',
    'WI',
  ] as StateCode[],

  // Standard ICC24-1110 states with variations
  ICC_MINIMAL: ['MD', 'OR'] as StateCode[],
  ICC_SHORT_PERIOD: ['KS'] as StateCode[],
  ICC_MAX_SHORT: ['VT'] as StateCode[],

  // State-specific product IDs
  CUSTOM: {
    AZ: { productId: '1110-AZ', rules: 'minimalWithStandardPeriod' as RuleSetKey },
    CT: { productId: '1110-CT', rules: 'standardWithShortPeriod' as RuleSetKey },
    DC: { productId: '1110-DC-0925', rules: 'standard' as RuleSetKey },
    DE: { productId: '1110-DE', rules: 'standard' as RuleSetKey },
    IN: { productId: '1110-IN', rules: 'standard' as RuleSetKey },
    MT: { productId: '1110-MT', rules: 'standard' as RuleSetKey },
    ND: { productId: '1110-ND', rules: 'standard' as RuleSetKey },
    SC: { productId: '1110-SC', rules: 'standard' as RuleSetKey },
    SD: { productId: '1110-SD', rules: 'maxWithShortPeriod' as RuleSetKey },
  },
};

export const carescoutStatesToProductIdMap: Record<StateCode, StateProduct> = {
  // Build standard ICC24-1110 states
  ...Object.fromEntries(
    CARESCOUT_STATE_CONFIGS.STANDARD_ICC.map(state => [
      state,
      { productId: 'ICC24-1110', rules: CARESCOUT_RULE_SETS.standard },
    ]),
  ),

  // ICC24-1110 with minimal coverage
  ...Object.fromEntries(
    CARESCOUT_STATE_CONFIGS.ICC_MINIMAL.map(state => [
      state,
      { productId: 'ICC24-1110', rules: CARESCOUT_RULE_SETS.minimalWithStandardPeriod },
    ]),
  ),

  // ICC24-1110 with short elimination period
  ...Object.fromEntries(
    CARESCOUT_STATE_CONFIGS.ICC_SHORT_PERIOD.map(state => [
      state,
      { productId: 'ICC24-1110', rules: CARESCOUT_RULE_SETS.standardWithShortPeriod },
    ]),
  ),

  // ICC24-1110 with max coverage and short period
  ...Object.fromEntries(
    CARESCOUT_STATE_CONFIGS.ICC_MAX_SHORT.map(state => [
      state,
      { productId: 'ICC24-1110', rules: CARESCOUT_RULE_SETS.maxWithShortPeriod },
    ]),
  ),

  // Custom state-specific products
  ...Object.fromEntries(
    Object.entries(CARESCOUT_STATE_CONFIGS.CUSTOM).map(([state, config]) => [
      state,
      { productId: config.productId, rules: CARESCOUT_RULE_SETS[config.rules] },
    ]),
  ),
};

export const EDWARD_JONES_ORGANIZATION_DISPLAY_NAME = 'Edward Jones';
export const EDWARD_JONES_ORGANIZATION_NAME = 'Edward D. Jones & Co., L.P.';

export const OPEN_AI_MODEL = 'gpt-5.4';
export const UNDERWRITING_PROMPT_TYPES = {
  NATIONWIDE_GUIDANCE: 'underwriting-nw-guidance',
  NATIONWIDE_PARTNER_GUIDANCE: 'underwriting-nw-partner-guidance',
  ONE_AMERICA_GUIDANCE: 'underwriting-oa-guidance',
  ONE_AMERICA_PARTNER_GUIDANCE: 'underwriting-oa-partner-guidance',
  CARESCOUT_GUIDANCE: 'underwriting-cs-guidance',
  LINCOLN_GUIDANCE: 'underwriting-lincoln-guidance',
  SECURIAN_GUIDANCE: 'underwriting-securian-guidance',
  NYL_ASSET_FLEX_GUIDANCE: 'underwriting-nyl-asset-flex-guidance',
} as const;

export const PUI_ANONYMIZATION_PROMPT_TYPE = 'pui-intake-anonymization';
export const PUI_ANONYMIZATION_PROMPT_VERSION = '1.1.0';
